import { inject, Injectable } from '@angular/core';
import { Actions } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { defer, EMPTY, first, forkJoin, map, merge, Observable, of, switchMap } from 'rxjs';

import { syncSelect } from '@blaze-shared/platform-data-access';

import { executeAICommands, selectAiExecutionError } from '../../+state/ai';
import { IconResolverService } from '../page';

import { AICommand, CommandType } from './base.commands';
import { CommandPayloadFactory } from './command-payload.factory';

@Injectable({ providedIn: 'root' })
export class AiCommandService {
  private readonly commandPayloadFactory = inject(CommandPayloadFactory);
  private readonly iconResolverService = inject(IconResolverService);
  private readonly store = inject(Store);
  private readonly reducedActions$ = inject(Actions);

  executeCommands(commands: AICommand<CommandType>[]): Observable<void> {
    if (commands.length === 0) {
      return of(undefined);
    }

    return defer(() => this.iconResolverService.waitForIconsToLoad()).pipe(
      switchMap(() => this.createAction(commands)),
      first(),
      switchMap((action) => this.dispatchAndObserveExecutors(action)),
    );
  }

  private dispatchAndObserveExecutors(action: Action): Observable<void> {
    return merge(
      this.reducedActions$.pipe(first((reduced) => reduced === action)),
      defer(() => {
        this.store.dispatch(action);
        return EMPTY;
      }),
    ).pipe(
      map(() => {
        const error = syncSelect(this.store, selectAiExecutionError);
        if (error) {
          throw new Error(error);
        }
        return undefined;
      }),
    );
  }

  private createAction(commands: AICommand<CommandType>[]): Observable<Action> {
    return forkJoin(
      commands.map((command) => this.commandPayloadFactory.createCommandPayload(command)),
    ).pipe(map((payload) => executeAICommands({ payload })));
  }
}
